// src/components/ProxyUserAdmin.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import OperatorDashboard from './OperatorDashboard';
import ClientDashboard from './ClientDashboard';
import { useOrgBrand, OrgBrandProvider } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import '../styles/ProxyUser.css';
import CustomSelect from './CustomSelect';

// Component that renders the proxied dashboard with its own OrgBrandContext
const ProxiedDashboardContent = ({ activeTab, selectedUser, onLogout }) => {
  const { reinitializePreferences } = useOrgBrand();

  // Initialize with proxied user's preferences when this component mounts
  useEffect(() => {
    reinitializePreferences();
  }, [reinitializePreferences]);

  if (activeTab === 'client') {
    return <ClientDashboard user={selectedUser} onLogout={onLogout} isProxyMode={true} />;
  } else {
    return <OperatorDashboard user={selectedUser} onLogout={onLogout} isProxyMode={true} />;
  }
};

const ProxyUserAdmin = ({ user, onLogout, dashboardData }) => {
  const modal = useModal();
  // Note: We don't need to access OrgBrandContext here since proxied dashboard has its own context
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('proxy_user_admin_active_tab') || 'client';
  });

  useEffect(() => {
    localStorage.setItem('proxy_user_admin_active_tab', activeTab);
  }, [activeTab]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [proxiedUserData, setProxiedUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Helper function to safely get string values
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  // Get clients for proxy (sorted alpha-numerically by name)
  const getClients = () => {
    const clients = dashboardData?.data?.clients || [];
    return [...clients].sort((a, b) => {
      const nameA = safeString(a.name).toLowerCase();
      const nameB = safeString(b.name).toLowerCase();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  // Get operators for proxy (sorted alpha-numerically by name)
  const getOperators = () => {
    const operators = dashboardData?.data?.operators || [];
    return [...operators].sort((a, b) => {
      const nameA = safeString(a.name).toLowerCase();
      const nameB = safeString(b.name).toLowerCase();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  // Handle user selection
  const handleUserSelect = async (userId) => {
    if (!userId) {
      await clearProxy();
      return;
    }

    setLoading(true);
    setSelectedUserId(userId);

    try {
      // Note: No need to save/clear admin state since proxied dashboard has its own OrgBrandContext
      // The admin's org/brand selection remains untouched in the outer context

      // Find the user from the dashboard data
      const users = activeTab === 'client' ? getClients() : getOperators();
      const foundUser = users.find(u => safeString(u._id) === userId);

      if (foundUser) {
        setSelectedUser(foundUser);

        // Collapse the admin's left nav when proxying into a user
        localStorage.setItem('adminSidebarCollapsed', JSON.stringify(true));
        // Dispatch event to trigger collapse
        window.dispatchEvent(new CustomEvent('collapseAdminNav'));

        // Use same proxy API as super admin
        await axios.post('/api/super-admin/assign-self-role', {
          user_id: userId
        });

        // Fetch the user data to populate the proxied view
        setProxiedUserData(foundUser);
      }
    } catch (error) {
      console.error('Error proxying as user:', error);
      modal.error('Failed to proxy as user: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Clear proxy mode
  const clearProxy = async () => {
    // Don't do anything if there's no proxied user
    if (!selectedUser && !selectedUserId) {
      return;
    }

    // Set transitioning flag to prevent dashboard from rendering during cleanup
    setIsTransitioning(true);

    // Immediately clear UI state to prevent rendering issues during transition
    setSelectedUserId('');
    setSelectedUser(null);
    setProxiedUserData(null);

    try {
      await axios.post('/api/super-admin/clear-role-assignments');

      // Note: No need to clear/restore admin's org/brand state
      // The admin's context remains untouched, only the inner proxied context is destroyed
    } catch (error) {
      console.error('Error clearing proxy:', error);
      modal.error('Failed to clear proxy mode');
    } finally {
      // Small delay to ensure old dashboard fully unmounts before new one mounts
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }
  };

  // Reset when tab changes (only if a user is actually proxied)
  useEffect(() => {
    if (selectedUser || selectedUserId) {
      clearProxy();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Clear proxy from session when the admin navigates away from this panel
  useEffect(() => {
    return () => {
      axios.post('/api/super-admin/clear-role-assignments', {}, { withCredentials: true })
        .catch(() => {});
    };
  }, []);

  // Add beforeunload listener to clear proxy when browser closes
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Synchronous cleanup before page unloads
      const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
      navigator.sendBeacon('/api/super-admin/clear-role-assignments', blob);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return (
    <div className="proxy-user-container">
      {/* Horizontal control bar */}
      <nav className="internal-nav">
        <div className="internal-nav-items">
          <button
            className={`internal-nav-btn ${activeTab === 'client' ? 'active' : ''}`}
            onClick={() => setActiveTab('client')}
            data-onboarding="clients-tab"
          >
            Clients
          </button>
          <button
            className={`internal-nav-btn ${activeTab === 'operator' ? 'active' : ''}`}
            onClick={() => setActiveTab('operator')}
            data-onboarding="operators-tab"
          >
            Operators
          </button>
        </div>

        <div className="proxy-selection">
          <CustomSelect
              id="user-select"
              value={selectedUserId}
              onChange={(e) => handleUserSelect(e.target.value)}
              disabled={loading}
            >
              <option value="">-- Select {activeTab === 'client' ? 'Client' : 'Operator'} --</option>
              {activeTab === 'client' && getClients().map(client => (
                <option key={safeString(client._id)} value={safeString(client._id)}>
                  {safeString(client.name)} ({safeString(client.email)})
                  {client.organization_name && ` - ${safeString(client.organization_name)}`}
                  {client.brand_name && ` / ${safeString(client.brand_name)}`}
                </option>
              ))}
              {activeTab === 'operator' && getOperators().map(operator => (
                <option key={safeString(operator._id)} value={safeString(operator._id)}>
                  {safeString(operator.name)} ({safeString(operator.email)})
                </option>
              ))}
            </CustomSelect>
          {selectedUser && (
            <button className="clear-proxy-btn" onClick={clearProxy}>
              Exit Proxy
            </button>
          )}
        </div>
      </nav>

      {/* Proxied User Dashboard */}
      {(loading || isTransitioning) && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading user view...</p>
        </div>
      )}

      {!loading && !isTransitioning && selectedUser && (
        <div className="proxied-dashboard">
          <div className="dashboard-wrapper">
            {/* Wrap in separate OrgBrandProvider so proxied user has their own org/brand context */}
            <OrgBrandProvider>
              <ProxiedDashboardContent
                activeTab={activeTab}
                selectedUser={selectedUser}
                onLogout={onLogout}
              />
            </OrgBrandProvider>
          </div>
        </div>
      )}

      {!loading && !isTransitioning && !selectedUser && (
        <div className="empty-proxy-state">
          <p>Select a {activeTab === 'client' ? 'client' : 'operator'} from the dropdown above to view their dashboard</p>
        </div>
      )}
    </div>
  );
};

export default ProxyUserAdmin;
